import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FileDown, Languages } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LANGUAGES } from '@/i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shows a legal document (terms | privacy) with on-demand translation and PDF download.
const LegalDocDialog = ({ docKey, open, onOpenChange, onAccept }) => {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState((i18n.language || 'es').slice(0, 2));
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !docKey) return;
    setLoading(true);
    axios.get(`${API}/legal/docs/${docKey}`, { params: { lang } })
      .then((res) => setDoc(res.data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [open, docKey, lang]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid={`legal-doc-dialog-${docKey}`}>
        <DialogHeader className="p-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-lg pr-6" data-testid="legal-doc-title">{doc?.title || '…'}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs">{doc?.updated ? `Última actualización: ${doc.updated}` : ''}</span>
            <span className="flex items-center gap-1 ml-auto">
              <Languages size={14} className="text-slate-500" />
              <select
                data-testid="legal-doc-lang-select"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="text-xs border border-slate-200 rounded px-1 py-0.5 bg-white"
              >
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-slate-800 space-y-4" data-testid="legal-doc-body">
          {loading && <p className="text-slate-500 text-xs">{t('auth.loading')}</p>}
          {!loading && doc?.translation_error && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Traducción no disponible ahora mismo; se muestra la versión original en español.</p>
          )}
          {!loading && doc?.sections?.map((s, i) => (
            <section key={i}>
              {s.heading && <h3 className="font-semibold text-slate-900 mb-1">{s.heading}</h3>}
              {s.paragraphs.map((p, j) => <p key={j} className="leading-relaxed mb-1">{p}</p>)}
            </section>
          ))}
        </div>
        <div className="p-4 border-t border-slate-100 flex flex-wrap gap-2 justify-between items-center">
          <a href={`${BACKEND_URL}/api/legal/docs/${docKey}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline flex items-center gap-1" data-testid="legal-doc-pdf">
            <FileDown size={14} /> PDF original (ES)
          </a>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="legal-doc-close">{t('common.close')}</Button>
            {onAccept && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { onAccept(); onOpenChange(false); }} data-testid="legal-doc-accept">
                {t('common.accept')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LegalDocDialog;
